import React from "react";
import Downshift, { StateChangeOptions } from "downshift";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faXmark, faSpinner } from "@fortawesome/free-solid-svg-icons";

export type SeparatorItem = {
    _isSeparator: true;
    _groupLabel: string;
};

function isSeparator<T>(item: T | SeparatorItem | null | undefined): item is SeparatorItem {
    return !!(item as SeparatorItem | null)?._isSeparator;
}

function freeSoloStateReducer<T>(
    state: { inputValue?: string },
    changes: StateChangeOptions<T>,
): StateChangeOptions<T> {
    switch (changes.type) {
        case Downshift.stateChangeTypes.touchEnd:
        case Downshift.stateChangeTypes.blurInput:
        case Downshift.stateChangeTypes.mouseUp:
            return { type: changes.type, inputValue: state.inputValue, isOpen: false };
        case Downshift.stateChangeTypes.keyDownEscape:
            return {
                type: changes.type,
                inputValue: "",
                isOpen: false,
                selectedItem: null,
            };
        default:
            return changes;
    }
}

interface Props<T> {
    items: (T | SeparatorItem)[];
    selectedItem: T | null;
    onChange: (item: T | null) => void;
    itemToString: (item: T | null) => string;
    getOptionKey: (item: T) => string;
    getOptionLabel?: (item: T) => React.ReactNode;
    getOptionSelected?: (selected: T | null, item: T) => boolean;
    onInputValueChange?: (val: string) => void;
    onBlur?: () => void;
    filterOptions?: (items: T[], inputValue: string) => T[];
    isItemDisabled?: (item: T) => boolean;
    loading?: boolean;
    placeholder?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    inputClassName?: string;
    invalid?: boolean;
    freeSolo?: boolean;
    inputValue?: string;
    id?: string;
    name?: string;
    disabled?: boolean;
}

export default function Combobox<T>(props: Props<T>) {
    const {
        items,
        selectedItem,
        onChange,
        itemToString,
        getOptionKey,
        getOptionLabel,
        getOptionSelected,
        onInputValueChange,
        onBlur,
        filterOptions,
        isItemDisabled,
        loading = false,
        placeholder,
        inputRef,
        inputClassName = "",
        invalid = false,
        freeSolo = false,
        inputValue: controlledInputValue,
        id,
        name,
        disabled,
    } = props;

    return (
        <Downshift
            selectedItem={selectedItem}
            onChange={onChange}
            itemToString={itemToString}
            onInputValueChange={onInputValueChange}
            inputValue={controlledInputValue}
            stateReducer={freeSolo ? freeSoloStateReducer : undefined}
        >
            {({
                clearSelection,
                getInputProps,
                getItemProps,
                getRootProps,
                getMenuProps,
                isOpen,
                highlightedIndex,
                inputValue,
                openMenu,
            }) => {
                const rendered: (T | SeparatorItem)[] = filterOptions
                    ? (filterOptions(
                          items.filter((item) => !isSeparator(item)) as T[],
                          inputValue ?? "",
                      ) as (T | SeparatorItem)[])
                    : items;
                return (
                    <div
                        {...getRootProps({}, { suppressRefError: true })}
                        className="relative w-full"
                    >
                        <div
                            className={[
                                "flex w-full min-w-0 items-center gap-2 rounded-md border bg-surface px-3 py-2 text-sm transition-colors",
                                invalid
                                    ? "border-danger focus-within:ring-1 focus-within:ring-danger/40"
                                    : "border-border focus-within:border-accent focus-within:ring-1 focus-within:ring-accent/40",
                            ].join(" ")}
                        >
                            <FontAwesomeIcon
                                icon={loading ? faSpinner : faSearch}
                                className={[
                                    "shrink-0 text-xs text-ink-muted",
                                    loading ? "animate-spin" : "",
                                ].join(" ")}
                            />
                            <input
                                {...getInputProps({
                                    role: "combobox",
                                    placeholder,
                                    onBlur,
                                    ref: inputRef,
                                    id,
                                    name,
                                    disabled,
                                    onFocus: (e) => {
                                        if (!e.currentTarget.value) {
                                            if (!isOpen) openMenu();
                                        } else {
                                            e.currentTarget.select();
                                        }
                                    },
                                })}
                                className={[
                                    "min-w-0 flex-1 bg-transparent text-ink placeholder:text-ink-muted focus:outline-none",
                                    inputClassName,
                                ].join(" ")}
                            />
                            {selectedItem !== null &&
                                selectedItem !== undefined &&
                                selectedItem !== "" && (
                                    <button
                                        type="button"
                                        onClick={() => clearSelection()}
                                        aria-label="clear"
                                        className="flex h-5 w-5 shrink-0 appearance-none items-center justify-center rounded-full! border-0 bg-transparent p-0 text-ink-muted hover:bg-surface-3 hover:text-ink"
                                    >
                                        <FontAwesomeIcon icon={faXmark} className="text-xs" />
                                    </button>
                                )}
                        </div>
                        <ul
                            {...getMenuProps()}
                            className={[
                                "absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-auto rounded-md border border-border bg-surface py-1 shadow-lg",
                                isOpen && rendered.length > 0 ? "" : "hidden",
                            ].join(" ")}
                        >
                            {isOpen &&
                                rendered.map((item, index) => {
                                    if (isSeparator(item)) {
                                        return (
                                            <li
                                                key={`separator-${item._groupLabel}-${index}`}
                                                className="cursor-default px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink-muted"
                                            >
                                                {item._groupLabel}
                                            </li>
                                        );
                                    }
                                    const typedItem = item as T;
                                    const disabled = isItemDisabled?.(typedItem) ?? false;
                                    const selected =
                                        getOptionSelected?.(selectedItem, typedItem) ?? false;
                                    const highlighted = highlightedIndex === index;
                                    if (disabled) {
                                        return (
                                            <li
                                                key={getOptionKey(typedItem)}
                                                className="cursor-not-allowed px-3 py-2 text-sm text-ink-muted opacity-60"
                                            >
                                                {getOptionLabel
                                                    ? getOptionLabel(typedItem)
                                                    : itemToString(typedItem)}
                                            </li>
                                        );
                                    }
                                    return (
                                        <li
                                            key={getOptionKey(typedItem)}
                                            {...getItemProps({
                                                item,
                                                index,
                                            })}
                                            className={[
                                                "cursor-pointer px-3 py-2 text-sm",
                                                highlighted ? "bg-surface-3" : "",
                                                selected ? "font-medium text-accent" : "text-ink",
                                            ].join(" ")}
                                        >
                                            {getOptionLabel
                                                ? getOptionLabel(typedItem)
                                                : itemToString(typedItem)}
                                        </li>
                                    );
                                })}
                        </ul>
                    </div>
                );
            }}
        </Downshift>
    );
}
